import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/KeycloakContext';
import { useCredit } from '../../contexts/CreditContext';
import { useNavigate } from 'react-router-dom';
import './Members.css';
import Toast from '../../ui/Toast';
import LeadgenLoading from '../../ui/LeadgenLoading.js';
import ActivityLog from './ActivityLog';
import Pagination from '../../ui/Pagination';
import Banner from '../../ui/Banner';
import axios from '../../api/axios';

const ROLE_OPTIONS = [
  { value: 'org_admin', label: 'Admin', icon: 'fas fa-shield-alt', color: '#2563eb' },
  { value: 'org_sales_manager', label: 'Sales Manager', icon: 'fas fa-user-tie', color: '#7c3aed' },
  { value: 'org_sales_member', label: 'Sales Member', icon: 'fas fa-user-check', color: '#059669' },
  { value: 'org_researcher', label: 'Researcher', icon: 'fas fa-binoculars', color: '#0891b2' },
  { value: 'org_viewer', label: 'Viewer', icon: 'fas fa-eye', color: '#64748b' },
];

// The Owner role is special: it can only be granted by the current workspace
// owner, and granting it TRANSFERS ownership (the previous owner becomes an
// admin — exactly one owner exists per workspace). It is therefore only
// offered in the role dropdown when the viewer may transfer ownership — see
// RoleDropdown's canAssignOwner prop.
const OWNER_ROLE_OPTION = {
  value: 'org_owner',
  label: 'Owner',
  icon: 'fas fa-key',
  color: '#d97706',
};

function getRoleLabel(role) {
  if (!role) return 'Member';
  const cleanRole = String(role).toLowerCase().trim();
  if (cleanRole === 'organization_admin' || cleanRole === 'org_admin' || cleanRole === 'admin')
    return 'Admin';
  if (cleanRole === 'org_sales_manager' || cleanRole === 'sales_manager') return 'Sales Manager';
  if (
    cleanRole === 'org_sales_member' ||
    cleanRole === 'sales_member' ||
    cleanRole === 'org_member' ||
    cleanRole === 'organization_user' ||
    cleanRole === 'member'
  )
    return 'Sales Member';
  if (cleanRole === 'org_researcher' || cleanRole === 'researcher') return 'Researcher';
  if (cleanRole === 'org_viewer' || cleanRole === 'viewer') return 'Viewer';
  if (cleanRole === 'organization_owner' || cleanRole === 'org_owner' || cleanRole === 'owner')
    return 'Owner';
  const found = ROLE_OPTIONS.find((r) => r.value === cleanRole);
  if (found) return found.label;
  return role
    .replace(/^org_/, '')
    .replace(/^organization_/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getRoleOption(role) {
  return (
    ROLE_OPTIONS.find((r) => r.value === role) || {
      value: role,
      label: getRoleLabel(role),
      icon: 'fas fa-user',
      color: '#64748b',
    }
  );
}

/**
 * Custom Dropdown for selecting member role in table rows (matching Image 2 style with Portal).
 */
const RoleDropdown = ({ member, isActionable, canAssignOwner = false, onRoleChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, minWidth: 160, flipUp: false });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // The Owner option appears only for viewers allowed to transfer ownership.
  const roleOptions = canAssignOwner ? [OWNER_ROLE_OPTION, ...ROLE_OPTIONS] : ROLE_OPTIONS;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuEstimatedHeight = roleOptions.length * 36 + 16;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < menuEstimatedHeight && rect.top > menuEstimatedHeight;

    setCoords({
      top: flipUp ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      minWidth: Math.max(rect.width, 160),
      flipUp,
    });
  }, [roleOptions.length]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    const handleScrollOrResize = () => {
      updatePosition();
    };

    const handleClickOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, updatePosition]);

  if (!isActionable) {
    if (member.is_owner || member.workspace_role === 'org_owner') {
      return (
        <span className="tm-role-pill tm-role-owner">
          <i className="fas fa-key tm-role-icon" />
          Owner
        </span>
      );
    }
    const currentOpt = getRoleOption(member.workspace_role);
    return (
      <span
        className={`tm-role-pill tm-role-${(member.workspace_role || '').replace('organization_', 'org_')}`}
      >
        <i className={`${currentOpt.icon} tm-role-icon`} />
        {currentOpt.label}
      </span>
    );
  }

  const currentOpt = getRoleOption(member.workspace_role);

  const handleSelect = (role) => {
    setIsOpen(false);
    if (role !== member.workspace_role) {
      onRoleChange(member, role);
    }
  };

  return (
    <div className={`tm-role-dropdown-container ${isOpen ? 'is-open' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`tm-role-trigger tm-role-trigger-${member.workspace_role} ${isOpen ? 'active' : ''}`}
        onClick={() => {
          if (!isOpen) updatePosition();
          setIsOpen((prev) => !prev);
        }}
        aria-expanded={isOpen}
      >
        <span className="tm-role-trigger-inner">
          <i className={`${currentOpt.icon} tm-role-trigger-icon`} />
          <span className="tm-role-trigger-label">{currentOpt.label}</span>
        </span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} tm-role-trigger-chevron`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="tm-role-menu-panel"
            style={{
              position: 'fixed',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.minWidth}px`,
              transform: coords.flipUp ? 'translateY(-100%)' : 'none',
              zIndex: 99999,
            }}
          >
            <div className="tm-role-menu-list">
              {roleOptions.map((opt) => {
                const isSelected =
                  member.workspace_role === opt.value ||
                  (member.workspace_role === 'org_member' && opt.value === 'org_sales_member');
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`tm-role-menu-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(opt.value)}
                  >
                    <i className={`${opt.icon} tm-role-menu-icon`} style={{ color: opt.color }} />
                    <span className="tm-role-menu-text">{opt.label}</span>
                    {isSelected && <i className="fas fa-check tm-role-menu-check" />}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

/**
 * Custom Dropdown for filters (Role / Status) matching Image 2 UI.
 */
const CustomFilterDropdown = ({ value, onChange, options, minWidth = 140, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, minWidth, flipUp: false });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuEstimatedHeight = options.length * 36 + 16;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < menuEstimatedHeight && rect.top > menuEstimatedHeight;

    setCoords({
      top: flipUp ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      minWidth: Math.max(rect.width, typeof minWidth === 'number' ? minWidth : 140),
      flipUp,
    });
  }, [options.length, minWidth]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    const handleScrollOrResize = () => {
      updatePosition();
    };

    const handleClickOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, updatePosition]);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <div className={`tm-custom-select-container ${isOpen ? 'is-open' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`tm-custom-select-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) updatePosition();
            setIsOpen((prev) => !prev);
          }
        }}
        style={{ minWidth }}
        disabled={disabled}
        aria-expanded={isOpen}
      >
        {selectedOption?.icon && (
          <i
            className={`${selectedOption.icon} tm-custom-select-trigger-icon`}
            style={selectedOption.color ? { color: selectedOption.color } : {}}
          />
        )}
        <span className="tm-custom-select-label">{selectedOption?.label || value}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} tm-custom-select-chevron`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="tm-custom-select-panel"
            style={{
              position: 'fixed',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.minWidth}px`,
              transform: coords.flipUp ? 'translateY(-100%)' : 'none',
              zIndex: 99999,
            }}
          >
            <div className="tm-custom-select-list">
              {options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`tm-custom-select-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                  >
                    {opt.icon && (
                      <i
                        className={`${opt.icon} tm-custom-select-item-icon`}
                        style={opt.color ? { color: opt.color } : {}}
                      />
                    )}
                    <span className="tm-custom-select-item-text">{opt.label}</span>
                    {isSelected && <i className="fas fa-check tm-custom-select-item-check" />}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

/**
 * Members Page Component — Team Management
 */
function Members() {
  const { keycloak } = useAuth();
  const currentUserId = keycloak?.tokenParsed?.sub;
  const {
    workspaceId,
    workspaceName,
    canManageMembers,
    isOrganizationAdmin,
    isOwner,
    isSuperadmin,
    primaryWorkspace,
    refreshWorkspaceContext,
  } = useWorkspace();

  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [signupLink, setSignupLink] = useState(null); // shown when email can't be sent

  // Section (top-level combined page tabs)
  const [section, setSection] = useState('team'); // 'team' | 'activity-logs'

  // Tabs
  const [activeTab, setActiveTab] = useState('members');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageInput, setPageInput] = useState('1');

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org_member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  // Delete modal
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [inviteToCancel, setInviteToCancel] = useState(null);
  const [cancellingInvite, setCancellingInvite] = useState(false);
  const [roleChangeConfirm, setRoleChangeConfirm] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Credit Member Status tab
  const [creditMembers, setCreditMembers] = useState([]);
  const [creditLoading, setCreditLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  // ——————————————————————————————————————————————————————————————————————————
  // Fetch members
  // ——————————————————————————————————————————————————————————————————————————
  const fetchMembers = useCallback(async () => {
    if (!workspaceId || !keycloak?.token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      const data = await response.json();
      if (data.success) {
        setMembers(data.members || []);
      } else {
        setToast({ show: true, message: data.message || 'Failed to load members', type: 'error' });
      }
    } catch {
      setToast({ show: true, message: 'Failed to connect to server', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, keycloak?.token, API_URL]);

  // Fetch credit member status for the organization
  const fetchCreditMemberStatus = useCallback(async () => {
    if (!keycloak?.token) return;
    setCreditLoading(true);
    try {
      const response = await fetch(`${API_URL}/credits/org/members/status`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setCreditMembers(data.data || []);
      } else {
        const err = await response.json().catch(() => ({}));
        setToast({
          show: true,
          message: err.message || 'Failed to fetch credit status',
          type: 'error',
        });
      }
    } catch {
      setToast({ show: true, message: 'Failed to fetch credit status', type: 'error' });
    } finally {
      setCreditLoading(false);
    }
  }, [keycloak?.token, API_URL]);

  const fetchCreditMembers = fetchCreditMemberStatus;

  useEffect(() => {
    fetchMembers();
    if (section === 'member-status') {
      fetchCreditMemberStatus();
    }
  }, [fetchMembers, fetchCreditMemberStatus, section]);

  // Reset pagination when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter, itemsPerPage]);

  // ——————————————————————————————————————————————————————————————————————————
  // Invite
  // ——————————————————————————————————————————————————————————————————————————
  const handleInvite = async (e) => {
    e.preventDefault();
    const normalized = inviteEmail.trim().toLowerCase();
    if (!normalized) return;

    // Check if email already has a pending invitation
    const pendingInvite = members.find(
      (m) => m.email?.toLowerCase() === normalized && m.membership_status === 'pending_invite'
    );
    if (pendingInvite) {
      setInviteError(
        'This email already has a pending invitation. You cannot invite it again unless you delete the existing invitation from the members list.'
      );
      return;
    }

    // Check if email is already an active member
    const existingMember = members.find(
      (m) => m.email?.toLowerCase() === normalized && m.membership_status !== 'pending_invite'
    );
    if (existingMember) {
      setInviteError('This user is already an active member of this workspace.');
      return;
    }

    setInviting(true);
    setInviteError(null);

    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${keycloak.token}`,
        },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setToast({
          show: true,
          message: data.message || `Invitation sent to ${inviteEmail}`,
          type: 'success',
        });
        setInviteEmail('');
        setShowInviteModal(false);
        fetchMembers();
      } else {
        const msg = data.error || data.message || 'Failed to send invitation';
        setInviteError(msg);
      }
    } catch {
      setInviteError('Network error — please check your connection and try again');
    } finally {
      setInviting(false);
    }
  };

  // ——————————————————————————————————————————————————————————————————————————
  // Role change with custom confirmation modal
  // ——————————————————————————————————————————————————————————————————————————
  const requestRoleChange = (member, newRole) => {
    if (newRole === member.workspace_role) return;

    if (member.membership_status === 'pending_invite') {
      setToast({
        show: true,
        message: 'Cannot change role of a pending invitation. Cancel and re-invite.',
        type: 'error',
      });
      return;
    }

    const previousRoleLabel = getRoleLabel(member.workspace_role);
    const newRoleLabel = getRoleLabel(newRole);

    setRoleChangeConfirm({
      userId: member.user_id,
      userName: member.display_name || member.username || member.email || 'Member',
      currentRoleLabel: previousRoleLabel,
      newRoleLabel,
      memberCredit: member.credit_balance || 0,
      newRole,
      ownershipTransfer: newRole === 'org_owner',
    });
  };

  const handleCancelRoleChange = () => {
    setRoleChangeConfirm(null);
  };

  const handleConfirmRoleChange = async () => {
    if (!roleChangeConfirm) return;
    const { userId, newRole } = roleChangeConfirm;
    setRoleChangeConfirm(null);

    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${keycloak.token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setToast({
          show: true,
          message: result.message || 'Role updated successfully',
          type: 'success',
        });
        fetchMembers();
        // An ownership transfer also changes the current user's own role (the
        // previous owner becomes an admin) — refresh resolved context so
        // permission-gated UI reflects it immediately.
        refreshWorkspaceContext();
      } else {
        setToast({
          show: true,
          message: result.message || 'Failed to update role',
          type: 'error',
        });
      }
    } catch {
      setToast({ show: true, message: 'Failed to update role', type: 'error' });
    }
  };

  // ——————————————————————————————————————————————————————————————————————————
  // Remove
  // ——————————————————————————————————————————————————————————————————————————
  const handleRemove = (member) => {
    setMemberToRemove(member);
  };

  const confirmRemoveUser = async () => {
    if (!memberToRemove) return;

    setRemoving(true);
    const userId = memberToRemove.user_id;

    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/members/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setToast({ show: true, message: 'Member removed successfully', type: 'success' });
        fetchMembers();
      } else {
        setToast({
          show: true,
          message: result.message || 'Failed to remove member',
          type: 'error',
        });
      }
    } catch {
      setToast({ show: true, message: 'Failed to remove member', type: 'error' });
    } finally {
      setRemoving(false);
      setMemberToRemove(null);
    }
  };

  const handleCancelInvite = (member) => {
    setInviteToCancel(member);
  };

  const confirmCancelInvite = async () => {
    if (!inviteToCancel) return;
    setCancellingInvite(true);
    try {
      const response = await fetch(
        `${API_URL}/workspace/${workspaceId}/members/invite/${inviteToCancel.membership_id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${keycloak.token}` },
        }
      );
      const result = await response.json();
      if (response.ok && result.success) {
        setToast({ show: true, message: 'Invitation cancelled successfully', type: 'success' });
        fetchMembers();
      } else {
        setToast({
          show: true,
          message: result.message || 'Failed to cancel invitation',
          type: 'error',
        });
      }
    } catch {
      setToast({ show: true, message: 'Failed to cancel invitation', type: 'error' });
    } finally {
      setCancellingInvite(false);
      setInviteToCancel(null);
    }
  };

  // ——————————————————————————————————————————————————————————————————————————
  const formatRole = (role) => getRoleLabel(role);

  const getMemberStatus = (member) => {
    if (member.membership_status === 'pending_invite') return 'invited';
    if (member.kc_enabled && member.email_verified) return 'active';
    if (member.kc_enabled && !member.email_verified) return 'pending';
    return 'inactive';
  };

  const getInitials = (name) => {
    if (!name || name === 'Unknown') return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // ——————————————————————————————————————————————————————————————————————————
  const filteredMembers = members
    .filter((m) => {
      const name = m.display_name || m.email || '';
      const matchSearch =
        searchTerm === '' ||
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchRole =
        roleFilter === 'all' ||
        m.workspace_role === roleFilter ||
        (roleFilter === 'org_sales_member' && m.workspace_role === 'org_member');
      const status = getMemberStatus(m);
      const matchStatus = statusFilter === 'all' || status === statusFilter;
      return matchSearch && matchRole && matchStatus;
    })
    .sort((a, b) => {
      const aOwner = !!(a.is_owner || a.workspace_role === 'org_owner');
      const bOwner = !!(b.is_owner || b.workspace_role === 'org_owner');
      if (aOwner && !bOwner) return -1;
      if (!aOwner && bOwner) return 1;
      return 0;
    });

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage) || 1;
  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const isActionable = useCallback(
    (m) => {
      if (m.is_owner) return false;
      if (m.user_id === currentUserId) return false;
      if (isSuperadmin || isOwner) return true;
      if (isOrganizationAdmin) return m.workspace_role !== 'org_admin' && !m.is_owner;
      return false;
    },
    [currentUserId, isSuperadmin, isOwner, isOrganizationAdmin]
  );

  // Only the current workspace owner (or a platform superadmin) may grant the
  // Owner role. Granting it transfers ownership: the previous owner becomes an
  // Admin and exactly one owner remains per workspace.
  const canTransferOwnership = Boolean(isOwner || isSuperadmin);

  // ——————————————————————————————————————————————————————————————————————————
  if (!canManageMembers) {
    return (
      <div className="tm-page">
        <div className="tm-access-denied">
          <i className="fas fa-lock"></i>
          <h3>Access Restricted</h3>
          <p>Only workspace admins can manage members.</p>
        </div>
      </div>
    );
  }

  const totalSeats = members.length;
  const seatLimit = primaryWorkspace?.seatLimit ?? null; // null = unlimited
  const ownerCount = members.filter(
    (m) =>
      m.is_owner || m.workspace_role === 'org_owner' || m.workspace_role === 'organization_owner'
  ).length;

  const adminCount = members.filter(
    (m) =>
      (m.workspace_role === 'org_admin' || m.workspace_role === 'organization_admin') &&
      !m.is_owner &&
      m.workspace_role !== 'org_owner' &&
      m.workspace_role !== 'organization_owner'
  ).length;

  const memberCount = members.filter(
    (m) =>
      !m.is_owner &&
      m.workspace_role !== 'org_owner' &&
      m.workspace_role !== 'organization_owner' &&
      m.workspace_role !== 'org_admin' &&
      m.workspace_role !== 'organization_admin'
  ).length;

  return (
    <div className="tm-page">
      {/* Page Title / Top banner */}
      <Banner title="Team Management" type="page-header">
        <div className="tm-credit-info">
          You are managing a&nbsp;
          <span className="tm-account-type-badge">
            <i className="fas fa-building" style={{ marginRight: '4px' }}></i> Workspace
          </span>
          &nbsp;team &nbsp;&nbsp;·&nbsp;&nbsp;
          <strong>{totalSeats}</strong>
          {seatLimit !== null && (
            <>
              {' '}
              / <strong>{seatLimit}</strong>
            </>
          )}
          &nbsp;seat{totalSeats > 1 ? 's' : ''} used &nbsp;&nbsp;({ownerCount} owner
          {ownerCount > 1 ? 's' : ''}, &nbsp;{adminCount} admin{adminCount > 1 ? 's' : ''}, &nbsp;
          {memberCount} member{memberCount > 1 ? 's' : ''})
        </div>
      </Banner>

      {/* Section Tab Bar */}
      <div className="tm-section-tabs">
        <button
          className={`tm-section-tab ${section === 'team' ? 'active' : ''}`}
          onClick={() => setSection('team')}
        >
          <i className="fas fa-users"></i> All User
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${section === 'member-status' ? 'active' : ''}`}
          onClick={() => {
            setSection('member-status');
            fetchCreditMembers();
          }}
        >
          <i className="fas fa-chart-bar"></i> Member Status
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${section === 'activity-logs' ? 'active' : ''}`}
          onClick={() => setSection('activity-logs')}
        >
          <i className="fas fa-history"></i> Activity Logs
        </button>
      </div>
      <div className="tm-horizontal-divider"></div>

      {/* Activity Logs Section */}
      {section === 'activity-logs' && (
        <div className="al-embedded">
          <ActivityLog embedded={true} />
        </div>
      )}

      {/* Team Management Section */}
      {section === 'team' && (
        <>
          {/* Alerts */}
          {toast.show && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, show: false })}
            />
          )}
          {/* Signup link banner — shown when email can't be sent (no SMTP configured) */}
          {signupLink && (
            <Banner
              type="info"
              onClose={() => setSignupLink(null)}
              className="tm-invite-link-banner"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                <strong>Share this invite link directly with the invitee:</strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <input
                    readOnly
                    value={signupLink}
                    onClick={(e) => e.target.select()}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #ccc',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      background: '#f8f9fa',
                      cursor: 'text',
                    }}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(signupLink);
                    }}
                    className="tm-btn tm-btn-sm"
                    title="Copy link"
                  >
                    <i className="fas fa-copy"></i> Copy
                  </button>
                </div>
              </div>
            </Banner>
          )}

          {/* Filter Bar */}
          <div className="tm-filter-bar">
            <div className="tm-search">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search for member..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="tm-search-clear" onClick={() => setSearchTerm('')}>
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            <CustomFilterDropdown
              value={roleFilter}
              onChange={setRoleFilter}
              options={[
                { value: 'all', label: 'All roles', icon: 'fas fa-users' },
                { value: 'org_admin', label: 'Admin', icon: 'fas fa-shield-alt', color: '#2563eb' },
                {
                  value: 'org_sales_manager',
                  label: 'Sales Manager',
                  icon: 'fas fa-user-tie',
                  color: '#7c3aed',
                },
                {
                  value: 'org_sales_member',
                  label: 'Sales Member',
                  icon: 'fas fa-user-check',
                  color: '#059669',
                },
                {
                  value: 'org_researcher',
                  label: 'Researcher',
                  icon: 'fas fa-search',
                  color: '#0891b2',
                },
                { value: 'org_viewer', label: 'Viewer', icon: 'fas fa-eye', color: '#64748b' },
              ]}
            />
            <CustomFilterDropdown
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All statuses', icon: 'fas fa-layer-group' },
                { value: 'active', label: 'Active', icon: 'fas fa-check-circle', color: '#10b981' },
                { value: 'pending', label: 'Pending', icon: 'fas fa-clock', color: '#f59e0b' },
                { value: 'inactive', label: 'Inactive', icon: 'fas fa-ban', color: '#94a3b8' },
              ]}
            />
            <button
              className="tm-invite-btn"
              onClick={() => {
                setInviteError(null);
                setShowInviteModal(true);
              }}
            >
              <i className="fas fa-plus"></i> Invite member
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="members-loading-container">
              <LeadgenLoading message="Loading members..." />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="tm-empty">
              <i className="fas fa-user-friends"></i>
              <p>
                {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                  ? 'No members match your filters.'
                  : 'No members yet. Invite team members to start collaborating.'}
              </p>
            </div>
          ) : (
            <Pagination
              columns={[
                { key: 'member', label: 'Member' },
                { key: 'role', label: 'Role' },
                { key: 'status', label: 'Status' },
                { key: 'actions', label: '', width: '70px' },
              ]}
              data={paginatedMembers}
              renderRow={(member) => {
                const isCurrentUser =
                  member.user_id === currentUserId ||
                  (keycloak?.tokenParsed?.email &&
                    member.email &&
                    member.email.toLowerCase() === keycloak.tokenParsed.email.toLowerCase());

                let displayName = '';
                if (member.first_name || member.last_name) {
                  displayName = `${member.first_name || ''} ${member.last_name || ''}`.trim();
                } else if (member.display_name && !member.display_name.includes('@')) {
                  displayName = member.display_name;
                } else if (member.username && !member.username.includes('@')) {
                  displayName = member.username;
                } else {
                  const raw = member.email || member.display_name || '';
                  const usernamePart = raw.split('@')[0] || '';
                  displayName = usernamePart || 'Member';
                }

                const status = getMemberStatus(member);
                const isSelected = false; // Selection removed
                const isAdmin = member.workspace_role === 'org_admin';

                return (
                  <tr
                    key={member.membership_id || member.user_id}
                    className={isSelected ? 'row-sel' : ''}
                  >
                    <td className="col-member">
                      <div className="tm-member-cell">
                        <div
                          className="tm-avatar"
                          style={{ background: isAdmin ? '#2563eb' : '#3b82f6' }}
                        >
                          {getInitials(displayName || member.email)}
                        </div>
                        <div className="tm-member-info">
                          <div className="tm-name-wrapper">
                            <span className="tm-name">{displayName}</span>
                            {isCurrentUser && <span className="tm-you-tag">You</span>}
                          </div>
                          <span className="tm-email">{member.email || '—'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-role">
                      <RoleDropdown
                        member={member}
                        isActionable={isActionable(member)}
                        canAssignOwner={canTransferOwnership}
                        onRoleChange={requestRoleChange}
                      />
                    </td>
                    <td className="col-credit">
                      <span className={`tm-credit-badge tm-credit-${status}`}>
                        <span className="tm-status-dot" />
                        {status === 'active'
                          ? 'Active'
                          : status === 'pending'
                            ? 'Pending'
                            : status === 'invited'
                              ? 'Invited'
                              : 'Inactive'}
                      </span>
                    </td>
                    <td className="col-act">
                      {isOrganizationAdmin && isActionable(member) && (
                        <button
                          type="button"
                          className="tm-remove-btn"
                          title={
                            member.membership_status === 'pending_invite'
                              ? 'Cancel invitation'
                              : 'Remove member'
                          }
                          onClick={() => {
                            if (member.membership_status === 'pending_invite') {
                              handleCancelInvite(member);
                            } else {
                              handleRemove(member);
                            }
                          }}
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(limit) => {
                setCurrentPage(1);
                setItemsPerPage(limit);
              }}
              totalItems={filteredMembers.length}
              itemsName="members"
              className="tm-pagination-card"
            />
          )}

          {/* Invite Modal */}
          {showInviteModal && (
            <div
              className="tm-overlay"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowInviteModal(false);
                  setInviteError(null);
                }
              }}
            >
              <div className="tm-modal">
                <div className="tm-modal-head">
                  <h3>
                    <i className="fas fa-user-plus"></i> Invite a new member
                  </h3>
                  <button
                    className="tm-modal-close"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteError(null);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <form onSubmit={handleInvite}>
                  <div className="tm-modal-body">
                    {inviteError && (
                      <div
                        className="tm-alert tm-alert-danger"
                        style={{
                          marginBottom: '16px',
                          fontSize: '14px',
                          borderLeft: '4px solid #ef4444',
                          background: '#fef2f2',
                          padding: '10px 14px',
                          color: '#b91c1c',
                          borderRadius: '4px',
                          textAlign: 'left',
                        }}
                      >
                        <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                        {inviteError}
                      </div>
                    )}
                    <div className="tm-field">
                      <label style={{ display: 'flex', alignItems: 'center' }}>
                        Email Address
                        <span
                          className="tm-info-trigger"
                          data-tooltip="Only unregistered users on the platform can be invited. They will receive an email notification."
                        >
                          <i
                            className="fas fa-info-circle"
                            style={{
                              marginLeft: '6px',
                              color: '#3b82f6',
                              cursor: 'help',
                              fontSize: '13px',
                            }}
                          ></i>
                        </span>
                      </label>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => {
                          setInviteEmail(e.target.value);
                          if (inviteError) setInviteError(null);
                        }}
                        placeholder="name@company.com"
                        required
                        disabled={inviting}
                        autoFocus
                      />
                      {inviteEmail.trim() &&
                        members.some(
                          (m) =>
                            m.email?.toLowerCase() === inviteEmail.trim().toLowerCase() &&
                            m.membership_status === 'pending_invite'
                        ) && (
                          <div
                            style={{
                              marginTop: '8px',
                              fontSize: '12.5px',
                              color: '#b45309',
                              background: '#fffbeb',
                              border: '1px solid #fde68a',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              textAlign: 'left',
                            }}
                          >
                            <i
                              className="fas fa-exclamation-triangle"
                              style={{ marginTop: '2px', flexShrink: 0 }}
                            />
                            <span>
                              <strong>Already Invited:</strong> An invitation is already pending for
                              this email. You cannot invite it again unless you delete or cancel the
                              existing invitation from the members list.
                            </span>
                          </div>
                        )}
                      {inviteEmail.trim() &&
                        members.some(
                          (m) =>
                            m.email?.toLowerCase() === inviteEmail.trim().toLowerCase() &&
                            m.membership_status !== 'pending_invite'
                        ) && (
                          <div
                            style={{
                              marginTop: '8px',
                              fontSize: '12.5px',
                              color: '#b91c1c',
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              textAlign: 'left',
                            }}
                          >
                            <i
                              className="fas fa-user-check"
                              style={{ marginTop: '2px', flexShrink: 0 }}
                            />
                            <span>
                              <strong>Already a Member:</strong> This user is already an active
                              member of this workspace.
                            </span>
                          </div>
                        )}
                    </div>
                    <div className="tm-field">
                      <label style={{ display: 'flex', alignItems: 'center' }}>
                        Role
                        <span
                          className="tm-info-trigger"
                          data-tooltip={
                            'Admin: Full workspace access.\nMember: Only Platform features access.'
                          }
                        >
                          <i
                            className="fas fa-info-circle"
                            style={{
                              marginLeft: '6px',
                              color: '#3b82f6',
                              cursor: 'help',
                              fontSize: '13px',
                            }}
                          ></i>
                        </span>
                      </label>
                      <CustomFilterDropdown
                        value={inviteRole}
                        onChange={setInviteRole}
                        minWidth="100%"
                        disabled={inviting}
                        options={[
                          {
                            value: 'org_sales_member',
                            label: 'Sales Member',
                            icon: 'fas fa-user-check',
                            color: '#059669',
                          },
                          {
                            value: 'org_sales_manager',
                            label: 'Sales Manager',
                            icon: 'fas fa-user-tie',
                            color: '#7c3aed',
                          },
                          {
                            value: 'org_researcher',
                            label: 'Researcher',
                            icon: 'fas fa-search',
                            color: '#0891b2',
                          },
                          {
                            value: 'org_viewer',
                            label: 'Viewer',
                            icon: 'fas fa-eye',
                            color: '#64748b',
                          },
                          ...(isSuperadmin || isOwner
                            ? [
                                {
                                  value: 'org_admin',
                                  label: 'Admin',
                                  icon: 'fas fa-shield-alt',
                                  color: '#2563eb',
                                },
                              ]
                            : []),
                        ]}
                      />
                    </div>
                  </div>
                  <div className="tm-modal-foot">
                    <button
                      type="button"
                      className="tm-btn tm-btn-outline"
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteError(null);
                      }}
                      disabled={inviting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="tm-btn tm-btn-primary"
                      disabled={
                        inviting ||
                        !inviteEmail.trim() ||
                        members.some(
                          (m) => m.email?.toLowerCase() === inviteEmail.trim().toLowerCase()
                        )
                      }
                    >
                      {inviting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Sending...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i> Send Invite
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {memberToRemove && (
            <div
              className="tm-overlay"
              onClick={(e) => e.target === e.currentTarget && !removing && setMemberToRemove(null)}
            >
              <div className="tm-modal tm-modal-custom" style={{ maxWidth: '440px' }}>
                <div className="tm-modal-head-modern">
                  <div className="tm-modal-icon-badge tm-badge-danger">
                    <i className="fas fa-user-minus" />
                  </div>
                  <button
                    type="button"
                    className="tm-modal-close"
                    onClick={() => !removing && setMemberToRemove(null)}
                    aria-label="Close"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>

                <div className="tm-modal-body-modern">
                  <h3 className="tm-modal-title-modern">Remove Member</h3>
                  <p className="tm-modal-desc-modern">
                    Are you sure you want to remove this member? They will immediately lose access
                    to this workspace and its resources.
                  </p>

                  <div className="tm-invite-preview-box">
                    <div className="tm-role-change-avatar tm-avatar-danger">
                      {getInitials(
                        memberToRemove.display_name ||
                          memberToRemove.username ||
                          memberToRemove.email ||
                          'User'
                      )}
                    </div>
                    <div className="tm-invite-preview-info">
                      <span className="tm-invite-preview-email">
                        {memberToRemove.display_name ||
                          memberToRemove.username ||
                          memberToRemove.email}
                      </span>
                      <span className="tm-invite-preview-role">
                        {formatRole(memberToRemove.workspace_role)}
                        {memberToRemove.email && memberToRemove.display_name
                          ? ` • ${memberToRemove.email}`
                          : ''}
                      </span>
                    </div>
                  </div>

                  {Number(memberToRemove.credit_balance) > 0 && (
                    <div className="tm-credit-return-note">
                      <i className="fas fa-coins" />
                      <span>
                        <strong>{memberToRemove.credit_balance} credits</strong> will be returned to
                        the organization pool.
                      </span>
                    </div>
                  )}
                </div>

                <div className="tm-modal-foot-modern">
                  <button
                    type="button"
                    className="tm-btn-modal-secondary"
                    onClick={() => setMemberToRemove(null)}
                    disabled={removing}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="tm-btn-modal-danger"
                    onClick={confirmRemoveUser}
                    disabled={removing}
                  >
                    {removing ? (
                      <>
                        <i className="fas fa-spinner fa-spin" /> Removing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash-alt" /> Remove Member
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Cancel Invitation Confirmation Modal */}
          {inviteToCancel && (
            <div
              className="tm-overlay"
              onClick={(e) =>
                e.target === e.currentTarget && !cancellingInvite && setInviteToCancel(null)
              }
            >
              <div className="tm-modal tm-modal-custom" style={{ maxWidth: '440px' }}>
                <div className="tm-modal-head-modern">
                  <div className="tm-modal-icon-badge tm-badge-danger">
                    <i className="fas fa-envelope-open-text" />
                  </div>
                  <button
                    type="button"
                    className="tm-modal-close"
                    onClick={() => !cancellingInvite && setInviteToCancel(null)}
                    aria-label="Close"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>

                <div className="tm-modal-body-modern">
                  <h3 className="tm-modal-title-modern">Cancel Invitation</h3>
                  <p className="tm-modal-desc-modern">
                    Are you sure you want to cancel the pending workspace invitation? The invitation
                    link will be permanently invalidated.
                  </p>

                  <div className="tm-invite-preview-box">
                    <div className="tm-invite-preview-avatar">
                      <i className="fas fa-envelope" />
                    </div>
                    <div className="tm-invite-preview-info">
                      <span className="tm-invite-preview-email">{inviteToCancel.email}</span>
                      <span className="tm-invite-preview-role">
                        Invited as <strong>{formatRole(inviteToCancel.workspace_role)}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="tm-modal-foot-modern">
                  <button
                    type="button"
                    className="tm-btn-modal-secondary"
                    onClick={() => setInviteToCancel(null)}
                    disabled={cancellingInvite}
                  >
                    Keep Invitation
                  </button>
                  <button
                    type="button"
                    className="tm-btn-modal-danger"
                    onClick={confirmCancelInvite}
                    disabled={cancellingInvite}
                  >
                    {cancellingInvite ? (
                      <>
                        <i className="fas fa-spinner fa-spin" /> Cancelling...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash-alt" /> Cancel Invitation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Role Change / Upgrade Confirmation Modal */}
          {roleChangeConfirm && (
            <div
              className="tm-overlay"
              onClick={(e) => e.target === e.currentTarget && handleCancelRoleChange()}
            >
              <div className="tm-modal tm-modal-custom" style={{ maxWidth: '460px' }}>
                <div className="tm-modal-head-modern">
                  <div className="tm-modal-icon-badge tm-badge-primary">
                    <i className="fas fa-user-shield" />
                  </div>
                  <button
                    type="button"
                    className="tm-modal-close"
                    onClick={handleCancelRoleChange}
                    aria-label="Close"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>

                <div className="tm-modal-body-modern">
                  <h3 className="tm-modal-title-modern">
                    {roleChangeConfirm.ownershipTransfer
                      ? 'Transfer Ownership'
                      : roleChangeConfirm.newRole === 'org_admin'
                        ? 'Upgrade to Admin'
                        : 'Change Member Role'}
                  </h3>
                  <p className="tm-modal-desc-modern">
                    {roleChangeConfirm.ownershipTransfer
                      ? `${roleChangeConfirm.userName} will become the workspace Owner and you will be changed to an Admin. Only one owner can exist per workspace.`
                      : roleChangeConfirm.newRole === 'org_admin'
                        ? `Are you sure you want to promote ${roleChangeConfirm.userName} to Admin? They will receive full administrative access to manage workspace members and settings.`
                        : `Are you sure you want to change ${roleChangeConfirm.userName}'s role? Their workspace access permissions will be updated immediately.`}
                  </p>

                  <div className="tm-role-change-card">
                    <div className="tm-role-change-user">
                      <div className="tm-role-change-avatar">
                        {getInitials(roleChangeConfirm.userName)}
                      </div>
                      <div className="tm-role-change-details">
                        <span className="tm-role-change-name">{roleChangeConfirm.userName}</span>
                        <span className="tm-role-change-sub">Workspace Member</span>
                      </div>
                    </div>

                    <div className="tm-role-transition-flow">
                      <div className="tm-role-flow-badge tm-role-flow-from">
                        <span className="tm-role-flow-label">Current</span>
                        <span className="tm-role-flow-value">
                          {roleChangeConfirm.currentRoleLabel}
                        </span>
                      </div>
                      <div className="tm-role-flow-arrow">
                        <i className="fas fa-arrow-right" />
                      </div>
                      <div className="tm-role-flow-badge tm-role-flow-to">
                        <span className="tm-role-flow-label">New Role</span>
                        <span className="tm-role-flow-value">{roleChangeConfirm.newRoleLabel}</span>
                      </div>
                    </div>
                  </div>

                  {Number(roleChangeConfirm.memberCredit) > 0 && (
                    <div className="tm-credit-return-note">
                      <i className="fas fa-info-circle" />
                      <span>
                        <strong>{roleChangeConfirm.memberCredit} credits</strong> currently assigned
                        will return to the shared organization pool.
                      </span>
                    </div>
                  )}
                </div>

                <div className="tm-modal-foot-modern">
                  <button
                    type="button"
                    className="tm-btn-modal-secondary"
                    onClick={handleCancelRoleChange}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="tm-btn-modal-primary"
                    onClick={handleConfirmRoleChange}
                  >
                    <i className="fas fa-check" />{' '}
                    {roleChangeConfirm.ownershipTransfer
                      ? 'Transfer Ownership'
                      : 'Confirm Role Change'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Member Status Section */}
      {section === 'member-status' && (
        <>
          {toast.show && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, show: false })}
            />
          )}
          <div className="credit-table-card">
            {creditLoading ? (
              <div className="loading-row">
                <div className="spinner"></div>
                <p>Loading member credit status...</p>
              </div>
            ) : (
              <>
                <table className="credit-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Role</th>
                      <th>Credit Balance</th>
                      <th>Usage</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditMembers.map((member) => (
                      <tr key={member.user_id}>
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">
                              {String(member.display_name || '?')
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div>
                              <div className="user-name-bold">
                                {member.display_name
                                  ? String(member.display_name)
                                  : 'Unknown Member'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span style={{ textTransform: 'capitalize' }}>
                            {member.role.replace('org_', '')}
                          </span>
                        </td>
                        <td>
                          {member.role === 'org_owner' ||
                          member.role === 'org_admin' ||
                          member.role === 'owner' ||
                          member.role === 'admin' ? (
                            <strong>Owner/Admin</strong>
                          ) : (
                            <strong>{member.credit_balance.toLocaleString()}</strong>
                          )}
                        </td>
                        <td style={{ width: '150px' }}>
                          <div className="status-progress-container">
                            <div
                              className={`status-progress-bar ${member.status}`}
                              style={{ width: `${member.usage_pct}%` }}
                            ></div>
                          </div>
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                            {member.usage_pct}% used
                          </div>
                        </td>
                        <td>
                          {member.role === 'org_owner' ||
                          member.role === 'org_admin' ||
                          member.role === 'owner' ||
                          member.role === 'admin' ? (
                            <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                          ) : (
                            <button
                              className="page-btn"
                              onClick={() => {
                                navigate(
                                  `/credit-management?tab=assign&search=${encodeURIComponent(member.display_name || '')}`
                                );
                              }}
                            >
                              Assign Credits
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {creditMembers.length === 0 && !creditLoading && (
                  <div className="empty-state">
                    <i className="fas fa-users"></i>
                    <p>No members found in this organization.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Members;
